<template>
  <div>
    <v-row>
      <v-col cols="8">
        {{ $t('search.textTitle') }}
        <text-name-information-card />
        <v-text-field
          class="test-title-search"
          v-model="textTitleSearch"
          placeholder="Text title"
          @keyup.enter="resetSearch"
          variant="outlined"
        />
        {{ $t('search.characterSequenceDescription') }}
        <transliteration-information-card />
        <v-text-field
          class="test-character-search"
          v-model="translitSearch"
          :placeholder="$t('search.characterSequences')"
          @keyup.enter="resetSearch"
          variant="outlined"
        ></v-text-field>
        <v-btn
          class="test-submit-button"
          @click="resetSearch"
          color="primary"
          :disabled="!canPerformSearch"
          >{{ $t('search.searchBtnText') }}</v-btn
        >
      </v-col>
      <v-col cols="4" class="align-self-end">
        <v-radio-group label="Transliteration Search Mode" v-model="useMode">
          <v-radio
            label="Respect No Word Boundaries"
            value="respectNoBoundaries"
          ></v-radio>
          <v-radio
            label="Respect Word Boundaries"
            value="respectBoundaries"
          ></v-radio>
          <v-radio
            label="Respect All Word Boundaries"
            value="respectAllBoundaries"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <oare-data-table
      :items-length="totalSearchResults"
      :headers="headers"
      :items="searchResults"
      item-value="uuid"
      :fetch-items="searchTexts"
      :watched-params="['translit', 'title', 'mode']"
      :default-rows="100"
      density="compact"
      :page-text="
        searchTotalLoading ? '{0}-{1} of Loading...' : '{0}-{1} of {2}'
      "
    >
      <template #[`item.name`]="{ item }">
        <router-link :to="`/epigraphies/${item.uuid}/${item.discourseUuids}`">
          {{ item.name }}
        </router-link>
      </template>
      <template #[`item.matches`]="{ item }">
        <div
          v-for="(match, index) in item.matches"
          :key="index"
          v-html="match"
        ></div>
      </template>
    </oare-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, computed, onMounted } from 'vue';
import { SearchTextsResultRow, SearchTextsResponse } from '@oare/types';
import TextNameInformationCard from './components/TextNameInformationCard.vue';
import TransliterationInformationCard from './components/TransliterationInformationCard.vue';
import { highlightedItem } from '../utils';
import useQueryParam from '@/hooks/useQueryParam';
import sl from '@/serviceLocator';
import { OareDataTableOptions } from '@/components/base/OareDataTable.vue';

export default defineComponent({
  name: 'TextsSearch',
  components: {
    TextNameInformationCard,
    TransliterationInformationCard,
  },
  setup() {
    const searchResults: Ref<SearchTextsResultRow[]> = ref([]);
    const searchLoading = ref(false);
    const searchTotalLoading = ref(false);
    const totalSearchResults = ref(0);

    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const router = sl.get('router');

    const translitQuery = useQueryParam('translit', '', true);
    const textTitleQuery = useQueryParam('title', '', true);
    const mode = useQueryParam('mode', 'respectAllBoundaries', true);
    const [getIsVisited, setIsVisited] = useQueryParam(
      'isVisited',
      '',
      true,
      true
    );

    const translitSearch = ref(translitQuery.value);
    const textTitleSearch = ref(textTitleQuery.value);
    const useMode = ref(mode.value);

    const headers = ref([
      { title: 'Text Name', key: 'name', sortable: false },
      { title: 'Matching Lines', key: 'matches', sortable: false },
    ]);

    const canPerformSearch = computed(() => {
      return !!(translitSearch.value.trim() || textTitleSearch.value.trim());
    });

    const searchTexts = async ({ page, rows }: OareDataTableOptions) => {
      if (
        canPerformSearch.value &&
        (useMode.value === 'respectNoBoundaries' ||
          useMode.value === 'respectBoundaries' ||
          useMode.value === 'respectAllBoundaries')
      ) {
        searchLoading.value = true;
        try {
          let { results }: SearchTextsResponse = await server.searchTexts({
            characters: translitSearch.value,
            textTitle: textTitleSearch.value,
            page,
            rows,
            mode: useMode.value,
          });

          if (results.length === 1 && getIsVisited() !== 'true') {
            actions.showSnackbar(
              'This page appears because the search resolved into only one text. If this is not the text intended, use the browser to return to the search page and simplify the search term.'
            );
            await setIsVisited('true');
            router.push(`/epigraphies/${results[0].uuid}`);
          } else {
            searchResults.value = results;
          }
        } catch (err) {
          actions.showErrorSnackbar(
            'Error searching texts. Please try again.',
            err as Error
          );
        } finally {
          searchLoading.value = false;
        }
      }
    };

    const searchTextsTotal = async () => {
      if (
        canPerformSearch.value &&
        (useMode.value === 'respectNoBoundaries' ||
          useMode.value === 'respectBoundaries' ||
          useMode.value === 'respectAllBoundaries')
      ) {
        searchTotalLoading.value = true;
        try {
          totalSearchResults.value = await server.searchTextsTotal({
            characters: translitSearch.value,
            textTitle: textTitleSearch.value,
            mode: useMode.value,
          });
        } catch (err) {
          actions.showErrorSnackbar(
            'Error getting texts total. Please try again.',
            err as Error
          );
        } finally {
          searchTotalLoading.value = false;
        }
      }
    };

    const resetSearch = async (resetVisited: boolean = true) => {
      totalSearchResults.value = -1;
      textTitleQuery.value = textTitleSearch.value;
      translitQuery.value = translitSearch.value;
      mode.value = useMode.value;
      if (resetVisited) {
        await setIsVisited('false');
      }
      searchTextsTotal();
    };

    onMounted(() => {
      resetSearch(false);
    });

    return {
      searchResults,
      textTitleSearch,
      translitSearch,
      searchLoading,
      totalSearchResults,
      canPerformSearch,
      headers,
      highlightedItem,
      searchTexts,
      resetSearch,
      searchTotalLoading,
      useMode,
    };
  },
});
</script>
